import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PageContainer from '../../components/container/PageContainer';
import Breadcrumb from '../../layouts/full-layout/breadcrumb/Breadcrumb';
import {
  receptionnerManyCmd,
  searchCmdPublicByRef,
  updateCmdMultiStatus,
  viewCmdFournisseur,
} from '../../redux/slices/commande-fournisseur.slice';
import {
  CmdFbStatusBg,
  CmdFournisseurStatus,
  retardExcludedStatuses,
} from '../../utils/commande.enums';
import CmdFbDetailsWidget from './CmdFbDetails.widget.js';
import { useQrCameraScanner } from '../../hooks/useQrCameraScanner.js';
import { ScanFeedbackToast, useHardwareQrScanner } from '../../hooks/useHardwareQrScannerV2.js';

// ---- MUI icons ----
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BlockIcon from '@mui/icons-material/Block';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TagIcon from '@mui/icons-material/Tag';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { MONO } from '../../utils/helper.js';

const BCrumb = [
  {
    to: '/App',
    title: 'Home',
  },
  {
    title: 'Edit en lots',
  },
];
const BCrumb_pickup = [
  {
    to: '/App',
    title: 'Home',
  },
  {
    title: 'Réception',
  },
];

const allowed_status = [
  CmdFournisseurStatus.A_SAISIR,
  CmdFournisseurStatus.EN_ETIQUETAGE,
  CmdFournisseurStatus.A_DISPATCHER_MG,
  CmdFournisseurStatus.ENVOYE_MG,
  CmdFournisseurStatus.EN_STOCK,
  CmdFournisseurStatus.PRETE,
  CmdFournisseurStatus.A_EXPEDIE,
  CmdFournisseurStatus.LIVRE,
];

const allowed_status_pickup = [CmdFournisseurStatus.ENVOYE_MG];

/* ==================================================================
 * Presentation helpers (flat colors, compact, no gradients)
 * ================================================================== */

const C = {
  ok: '#22c55e',
  err: '#ef4444',
  info: '#0ea5e9',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  amber: '#f59e0b',
  slate: '#64748b',
};

const cardBg = (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,.025)' : t.palette.background.paper);

const statusLabel = (s) => String(s ?? '---').replace(/[-_]/g, ' ');

const MiniChip = ({ color, icon, label, title, solid, textColor }) => (
  <Box
    title={title ?? (typeof label === 'string' ? label : undefined)}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      height: 22,
      px: 0.85,
      borderRadius: 1,
      bgcolor: solid ? color : alpha(color, 0.15),
      color: solid ? (textColor ?? '#fff') : color,
      fontSize: 11.5,
      fontWeight: 700,
      maxWidth: '100%',
      minWidth: 0,
      flexShrink: 0,
      '& svg': { fontSize: 14, flexShrink: 0 },
    }}
  >
    {icon}
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  </Box>
);

const IconTile = ({ icon, color, size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: 1.25,
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
      bgcolor: alpha(color, 0.15),
      color,
      '& svg': { fontSize: size * 0.62 },
    }}
  >
    {icon}
  </Box>
);

/* ==================================================================
 * Main container — data flow unchanged
 * ================================================================== */
const CmdBulkEditContainer = ({ pickup_mode }) => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.cmd_achat_store);
  const [lst_cmd, set_lst_cmd] = useState([]);
  const [lst_cmd_serach, set_lst_cmd_serach] = useState([]);
  const [sel_item, set_sel_item] = useState(null);
  const [show_details, set_show_details] = useState(false);
  const [sel_status, set_sel_status] = useState('');

  const onSearchCommande = (event, value, reason) => {
    dispatch(searchCmdPublicByRef(value))
      .unwrap()
      .then((res) => {
        if (Array.isArray(res)) {
          if (pickup_mode)
            set_lst_cmd_serach(res.filter((el) => allowed_status_pickup.includes(el?.status)));
          else set_lst_cmd_serach(res.filter((el) => allowed_status.includes(el?.status)));

          if (sel_item) {
            set_sel_item(res.find((el) => el?.ref == sel_item?.ref));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const onAddCmd = (value) => {
    if (value) set_lst_cmd([value, ...lst_cmd]);
  };
  const onDelCmd = (value) => {
    if (value) set_lst_cmd(lst_cmd.filter((el) => el?.idCmdFournisseur != value));
  };

  const onUpdateMultiStatus = () => {
    if (pickup_mode && lst_cmd?.length)
      dispatch(
        receptionnerManyCmd({
          ids: lst_cmd.map((el) => el?.idCmdFournisseur),
        }),
      )
        .unwrap()
        .then((res) => {
          set_lst_cmd([]);
          set_lst_cmd_serach([]);
        })
        .catch((err) => console.log(err));
    else if (lst_cmd?.length && sel_status)
      dispatch(
        updateCmdMultiStatus({
          ids: lst_cmd.map((el) => el?.idCmdFournisseur),
          status: sel_status,
        }),
      )
        .unwrap()
        .then((res) => {
          set_lst_cmd([]);
          set_lst_cmd_serach([]);
        })
        .catch((err) => console.log(err));
  };

  const onShowDetails = (data) => {
    set_sel_item(data);
    set_show_details(true);
  };
  const onCloseDetails = () => {
    set_sel_item(null);
    set_show_details(false);
  };

  const { openScanner, QrScannerModal, closeScanner } = useQrCameraScanner({
    onScan: (value) => {
      const data = JSON.parse(value);
      if (data?.type === 'bon-commande' && data?.id) {
        dispatch(viewCmdFournisseur(data?.id))
          .unwrap()
          .then((res) => {
            if (pickup_mode && allowed_status_pickup.includes(res?.status)) onAddCmd(res);
            else if (allowed_status.includes(res?.status) && !pickup_mode) onAddCmd(res);

            closeScanner();
          })
          .catch((err) => console.log(err));
      }
    },
  });

  const handleScan = (value) => {
    const data = JSON.parse(value);
    if (data?.type === 'bon-commande' && data?.id) {
      dispatch(viewCmdFournisseur(data?.id))
        .unwrap()
        .then((res) => {
          if (pickup_mode && allowed_status_pickup.includes(res?.status)) onAddCmd(res);
          else if (allowed_status.includes(res?.status) && !pickup_mode) onAddCmd(res);
          closeScanner();
        })
        .catch((err) => console.log(err));
    }
  };

  const { lastScan } = useHardwareQrScanner(
    (data, raw) => {
      if (!data) {
        console.warn('Malformed scan:', raw);
        return;
      }
      handleScan(data);
    },
    { parseJson: true },
  );

  /* ---- display-only ---- */
  const nb = lst_cmd?.length ?? 0;
  const apply_disabled = nb == 0 || loading;
  const need_status = !pickup_mode && nb > 0 && !sel_status;

  return (
    <PageContainer title="Réception des commandes" description="">
      {/* breadcrumb */}
      <Breadcrumb
        title={pickup_mode ? 'Réception des commandes' : 'Modifier par lots'}
        items={pickup_mode ? BCrumb_pickup : BCrumb}
      />
      {/* end breadcrumb */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* ---------- Search + scanner ---------- */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexWrap: 'wrap',
            p: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: cardBg,
          }}
        >
          <IconTile
            icon={pickup_mode ? <MoveToInboxIcon /> : <PlaylistAddCheckIcon />}
            color={pickup_mode ? C.ok : C.indigo}
            size={36}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2 }}>
              {pickup_mode ? 'Réception' : 'Modification par lots'}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              Recherchez ou scannez les commandes à traiter
            </Typography>
          </Box>

          <Autocomplete
            sx={{ flex: '1 1 320px', minWidth: 240 }}
            options={Array.isArray(lst_cmd_serach) ? lst_cmd_serach : []}
            getOptionLabel={(el) =>
              el
                ? `${el?.ref} - ${
                    el?.client
                      ? `${el?.client?.nom} ${el?.client?.prenom}`
                      : el?.fournisseur?.nomSociete
                  }`
                : ''
            }
            loading={loading}
            noOptionsText="Aucune commande trouvée"
            loadingText="Recherche..."
            onInputChange={onSearchCommande}
            onChange={(event, newValue) => {
              return onAddCmd(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Num Commande / client ..."
                aria-label="fb"
                sx={{ '& .MuiInputBase-root': { fontSize: '0.88rem', borderRadius: 1.5 } }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 19 }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Button
            size="small"
            onClick={openScanner}
            variant="outlined"
            color="primary"
            startIcon={<QrCodeScannerIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}
          >
            Scanner QR (CAM)
          </Button>
          <QrScannerModal />
          <ScanFeedbackToast scanner={lastScan} />
        </Box>

        {/* ---------- List ---------- */}
        <CmdList
          loading={loading}
          error={error}
          lst_items={lst_cmd}
          onShowCmdDetails={onShowDetails}
          onUpdateMultiStatus={onUpdateMultiStatus}
          onDelItem={onDelCmd}
        />

        {/* ---------- Sticky action bar ---------- */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 8,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: nb ? alpha(C.ok, 0.5) : 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <MiniChip
            color={nb ? C.ok : C.slate}
            icon={<FactCheckOutlinedIcon />}
            label={`${nb} commande${nb > 1 ? 's' : ''} sélectionnée${nb > 1 ? 's' : ''}`}
          />

          <Box sx={{ flex: 1 }} />

          {!pickup_mode && (
            <TextField
              size="small"
              select
              value={sel_status}
              onChange={(e) => set_sel_status(e?.target?.value)}
              label={'Status'}
              disabled={lst_cmd?.length == 0 || loading}
              sx={{ width: { xs: '100%', sm: 260 }, '& .MuiInputBase-root': { fontSize: '0.88rem', borderRadius: 1.5 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FlagOutlinedIcon sx={{ fontSize: 19 }} />
                  </InputAdornment>
                ),
              }}
            >
              {allowed_status.map((el) => (
                <MenuItem key={`select-status-${el}`} value={el}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: CmdFbStatusBg?.[el] || 'gray',
                        flexShrink: 0,
                      }}
                    />
                    <Box component="span" sx={{ textTransform: 'capitalize' }}>
                      {statusLabel(el)}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}

          {need_status && (
            <MiniChip color={C.amber} icon={<InfoOutlinedIcon />} label="Choisissez un statut" />
          )}

          <Button
            onClick={onUpdateMultiStatus}
            disabled={apply_disabled}
            variant="contained"
            color="success"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : pickup_mode ? (
                <MoveToInboxIcon />
              ) : (
                <DoneAllIcon />
              )
            }
            sx={{ px: 3, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            {pickup_mode ? 'Réceptionner' : 'Appliquer'}
          </Button>
        </Box>
      </Box>

      <CmdFbDetailsWidget
        idCmdFournisseur={sel_item?.idCmdFournisseur}
        show={show_details}
        onClose={onCloseDetails}
      />
    </PageContainer>
  );
};

/* ==================================================================
 * List — one compact row per order (no horizontal scroll)
 * ================================================================== */
const COLS =
  '44px minmax(190px,1.4fr) minmax(200px,1.6fr) minmax(140px,1fr) minmax(130px,0.9fr) 150px 100px 100px';

const HEADERS = [
  ['', null],
  ['Num Commande', <TagIcon />],
  ['Article', <Inventory2OutlinedIcon />],
  ['Client', <PersonOutlineIcon />],
  ['Fournisseur', <LocalShippingOutlinedIcon />],
  ['Status', <FlagOutlinedIcon />],
  ['Date Prévu', <EventOutlinedIcon />],
  ['Magasin', <StorefrontOutlinedIcon />],
];

const CmdList = ({ lst_items, loading, error, onShowCmdDetails, onDelItem }) => {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: cardBg,
        overflow: 'hidden',
      }}
    >
      {/* column headers (wide screens) */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'grid' },
          gridTemplateColumns: COLS,
          columnGap: 1.5,
          alignItems: 'center',
          px: 2,
          height: 36,
          bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
        }}
      >
        {HEADERS.map(([label, icon], i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: 'text.secondary', '& svg': { fontSize: 15 } }}>
            {icon}
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {lst_items.length > 0 ? (
        lst_items.map((el) => (
          <CmdItem
            key={el?.idCmdFournisseur}
            cmd={el}
            onItemClick={onShowCmdDetails}
            onDelItem={onDelItem}
          />
        ))
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            py: 6,
            color: 'text.disabled',
          }}
        >
          <QrCode2Icon sx={{ fontSize: 44 }} />
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 700 }}>Aucune commande dans la liste</Typography>
          <Typography sx={{ fontSize: '0.76rem' }}>
            Scannez un bon de commande ou recherchez une commande ci-dessus
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// label shown only below the "lg" breakpoint (column header is hidden there)
const Cell = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        display: { xs: 'block', lg: 'none' },
        fontSize: '0.6rem',
        fontWeight: 700,
        color: 'text.disabled',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const CmdItem = ({ cmd, onItemClick, onDelItem }) => {
  const [blocage_tags, set_blocage_tags] = useState([]);

  useEffect(() => {
    if (Array.isArray(cmd?.bloquages)) {
      let tmp = cmd?.bloquages?.filter((el) => el?.status != 'resolus')?.map((el) => el?.type);
      tmp = [...new Set(tmp)];
      set_blocage_tags(tmp);
    }
  }, [cmd?.bloquages]);

  const statusColor = CmdFbStatusBg?.[cmd?.status] ? CmdFbStatusBg?.[cmd?.status] : 'gray';
  const is_late = cmd?.is_retard && !retardExcludedStatuses.includes(cmd?.status);
  const has_issue = !!cmd?.is_urgent || is_late || (Array.isArray(blocage_tags) && blocage_tags.length > 0);
  const stripColor = has_issue ? C.err : statusColor;
  const date_prevu =
    cmd?.is_urgent && cmd?.date_urgent
      ? String(cmd?.date_urgent).slice(0, 10)
      : cmd?.date_livraison_prevu
        ? String(cmd?.date_livraison_prevu).slice(0, 10)
        : '-';
  const article_name = `${cmd?.catalogue?.nom} ${
    cmd?.catalogue?.groupe_model?.name
      ? `(${cmd?.catalogue?.groupe_model?.name} ${cmd?.catalogue?.groupe_model_var})`
      : ''
  }`;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', lg: COLS },
        columnGap: 1.5,
        rowGap: 1,
        alignItems: 'center',
        px: 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        transition: 'background-color .15s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 8,
          bottom: 8,
          width: 3,
          borderRadius: 2,
          bgcolor: stripColor,
        },
        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
      }}
    >
      <Box>
        <IconButton
          color="error"
          size="small"
          title="Retirer de la liste"
          aria-label="Retirer de la liste"
          onClick={(e) => onDelItem(cmd?.idCmdFournisseur)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Cell label="Num Commande">
        <Box
          role="button"
          tabIndex={0}
          title="Voir les détails"
          onClick={(e) => onItemClick(cmd)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onItemClick(cmd);
            }
          }}
          sx={{
            cursor: 'pointer',
            minWidth: 0,
            '&:hover .cmd-ref': { textDecoration: 'underline' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2, borderRadius: 1 },
          }}
        >
          <Typography className="cmd-ref" noWrap sx={{ fontFamily: MONO, fontWeight: 800, fontSize: '0.86rem', color: 'primary.main' }}>
            {cmd?.ref}
          </Typography>
          {cmd?.ref_monday && (
            <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.72rem', color: 'text.secondary' }}>
              {cmd?.ref_monday}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: has_issue ? 0.5 : 0 }}>
            {cmd?.is_urgent && <MiniChip color={C.err} icon={<WhatshotIcon />} label="Urgent" />}
            {Array.isArray(blocage_tags) &&
              blocage_tags?.map((el, idx) => (
                <MiniChip key={`bl-${idx}-${el}`} color={C.err} icon={<BlockIcon />} label={`! ${el}`} />
              ))}
            {is_late && (
              <MiniChip
                color={C.err}
                icon={<EventBusyIcon />}
                label={`Retard (${Math.abs(+cmd?.nbr_j_retard)} J)`}
              />
            )}
          </Box>
        </Box>
      </Cell>

      <Cell label="Article">
        <Typography noWrap title={article_name} sx={{ fontWeight: 700, fontSize: '0.86rem' }}>
          {article_name}
        </Typography>
        <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'success.main', fontFamily: MONO }}>
          {cmd?.catalogue?.sku}
        </Typography>
      </Cell>

      <Cell label="Client">
        {cmd?.client ? (
          <MiniChip
            color={cmd?.fournisseur?.bg_color || 'gray'}
            solid
            icon={<PersonOutlineIcon />}
            label={`${cmd?.client?.nom} ${cmd?.client?.prenom}`}
          />
        ) : (
          <Typography sx={{ color: 'text.disabled' }}>—</Typography>
        )}
      </Cell>

      <Cell label="Fournisseur">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
            {cmd?.fournisseur?.nomSociete || '-'}
          </Typography>
        </Box>
      </Cell>

      <Cell label="Status">
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 24,
            px: 1.25,
            borderRadius: 1,
            border: '1px solid',
            borderColor: statusColor,
            color: CmdFbStatusBg?.[cmd?.status] ? CmdFbStatusBg?.[cmd?.status] : 'text.secondary',
            bgcolor: alpha(statusColor === 'gray' ? '#808080' : statusColor, 0.08),
            fontSize: '0.74rem',
            fontWeight: 700,
            textTransform: 'capitalize',
            maxWidth: '100%',
          }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cmd?.status || '---'}
          </Box>
        </Box>
      </Cell>

      <Cell label="Date Prévu">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: cmd?.is_urgent && cmd?.date_urgent ? C.err : 'text.secondary' }}>
          <EventOutlinedIcon sx={{ fontSize: 15 }} />
          <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.78rem', fontWeight: 600, color: 'inherit' }}>
            {date_prevu}
          </Typography>
        </Box>
      </Cell>

      <Cell label="Magasin">
        <MiniChip
          color={cmd?.magasin?.bg_color || 'gray'}
          solid
          textColor={cmd?.magasin?.nom ? '#000' : '#fff'}
          icon={<StorefrontOutlinedIcon />}
          label={cmd?.magasin?.code || 'central'}
        />
      </Cell>
    </Box>
  );
};

export default CmdBulkEditContainer;